// "Take photo" — next to "Upload session photo" (bizSAFE Audit Execution + Internal Audit worksheet). The photo is
// handed to the page's own upload (onPhoto(file)), so it is saved exactly like an uploaded photo.
//  • Phone / tablet: opens the device camera directly (the browser's own camera screen).
//  • Laptop: a small window with the webcam picture → Take photo → Use this photo / Retake.
import React, { useEffect, useRef, useState } from 'react'

const touchDevice = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches
const photoName = () => {
  const d = new Date(); const p = n => String(n).padStart(2, '0')
  return `session_photo_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.jpg`
}

export default function TakePhotoButton({ onPhoto, disabled, style }) {
  const [open, setOpen] = useState(false)
  const btnStyle = { cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13, background: 'var(--bg)', color: 'var(--accent)', border: '1px solid var(--accent)', opacity: disabled ? 0.5 : 1, ...style }

  if (touchDevice()) {
    return (
      <label style={btnStyle}>
        📷 Take photo
        <input type="file" accept="image/*" capture="environment" disabled={disabled} style={{ display: 'none' }}
          onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) onPhoto(f) }} />
      </label>
    )
  }
  return (
    <>
      <button type="button" style={btnStyle} disabled={disabled} onClick={() => setOpen(true)}>📷 Take photo</button>
      {open && <CameraWindow onClose={() => setOpen(false)} onUse={f => { setOpen(false); onPhoto(f) }} />}
    </>
  )
}

function CameraWindow({ onClose, onUse }) {
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const [error, setError] = useState('')
  const [shot, setShot] = useState(null)   // { blob, url }

  useEffect(() => {
    let cancelled = false
    async function start() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setError('This browser cannot use the camera. Please use “Upload session photo” instead.'); return }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }
        streamRef.current = stream
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}) }
      } catch (e) {
        setError(e && (e.name === 'NotAllowedError' || e.name === 'SecurityError')
          ? 'Camera access was blocked. Please allow the camera for this site in your browser (the camera icon in the address bar), or use “Upload session photo” instead.'
          : e && e.name === 'NotFoundError' ? 'No camera was found on this computer. Please use “Upload session photo” instead.'
          : 'Could not start the camera (it may be in use by another app, e.g. Teams or Zoom). Please close that app and try again, or use “Upload session photo”.')
      }
    }
    start()
    return () => { cancelled = true; if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()) }
  }, [])
  useEffect(() => () => { if (shot) URL.revokeObjectURL(shot.url) }, [shot])

  function take() {
    const v = videoRef.current
    if (!v || !v.videoWidth) return
    const c = document.createElement('canvas')
    c.width = v.videoWidth; c.height = v.videoHeight
    c.getContext('2d').drawImage(v, 0, 0)
    c.toBlob(blob => { if (blob) setShot({ blob, url: URL.createObjectURL(blob) }) }, 'image/jpeg', 0.9)
  }
  function use() {
    onUse(new File([shot.blob], photoName(), { type: 'image/jpeg' }))
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg)', borderRadius: 10, padding: 16, width: '100%', maxWidth: 720, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: 14 }}>Take session photo</strong>
          <button className="sm" onClick={onClose}>Close</button>
        </div>
        {error ? <div style={{ fontSize: 13, color: 'var(--danger)', padding: '12px 0' }}>{error}</div> : <>
          <div style={{ background: '#000', borderRadius: 8, overflow: 'hidden', aspectRatio: '16 / 9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* The live picture stays running behind the taken photo, so "Retake" is instant. */}
            <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'contain', display: shot ? 'none' : 'block' }} />
            {shot && <img src={shot.url} alt="Session" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            {shot
              ? <><button className="sm" onClick={() => setShot(null)}>Retake</button><button className="primary" onClick={use}>Use this photo</button></>
              : <button className="primary" onClick={take}>📸 Take photo</button>}
          </div>
        </>}
      </div>
    </div>
  )
}
